import { EWarrantServices, EWarrantTaskStatus } from 'data/eWarrant/eWarrant.types';

/**
 * A target the task status payload can address. `reference` is the LI task's LIID, which
 * the backend reads out of Xcipio with `asText("")` and falls back to the task:Reference of
 * the ETSI payload — so a task can still reach the UI without either, and such a target
 * cannot be decided at all.
 */
export const isAddressableTarget = (service: EWarrantServices): boolean => service.reference !== '';

/**
 * Xcipio's own EWSLITASK.TASKSTATUS value for a task whose time window has passed - never
 * one of the two values a decision writes (see EWarrantTaskStatus), and never written by us
 * either. Once a task reads this way Xcipio itself has taken it past deciding, whatever
 * decision it carried before.
 */
export const EXPIRED_TASK_STATUS = 'Expired';

/** Whether Xcipio has moved this target past its provisioning window on its own. */
export const isTargetExpired = (service: EWarrantServices): boolean =>
  service.taskStatus === EXPIRED_TASK_STATUS;

/**
 * Whether this target can still be decided - addressable, and not one Xcipio has already
 * taken past deciding by expiring it. The buttons on its own tab go read-only on exactly
 * this, and it is the same test that keeps an expired target from being asked for a
 * decision it can no longer take, in areAllTargetsApproved and getBlockingTargets below.
 */
export const isTargetDecidable = (service: EWarrantServices): boolean =>
  isAddressableTarget(service) && !isTargetExpired(service);

/**
 * The decision standing against each target, keyed by its LIID — the same `reference` the
 * task status payload addresses the EWSLITASK row by. A LIID absent from the map is
 * undecided.
 *
 * Unlike DocumentDecisions, which holds ticks the operator has not saved yet, this is a
 * record of what Xcipio already holds: a target's decision is written the moment its button
 * is clicked. The map exists so that write does not have to be followed by a re-read of the
 * whole e-warrant — see EWarrantView's submitTargetDecision.
 */
export type TargetDecisions = { [liid: string]: EWarrantTaskStatus };

/**
 * The decision recorded in Xcipio against one target, as `taskStatus` carries it — the
 * EWSLITASK.TASKSTATUS column, which is where a decision on a single target is written.
 *
 * Deliberately not `actualStatus`: that is the ETSI task:Status, and approving an update
 * moves the column without it, so reading the ETSI field here would show a decision that
 * has already been superseded. See buildServices in EWarrantServiceImpl.
 *
 * Anything outside the two decided values (AwaitingApproval, AwaitingProvisioning, an empty
 * column, Expired) is undecided rather than forced into one of them.
 */
const decisionOf = (service: EWarrantServices): EWarrantTaskStatus | null => {
  const status = service.taskStatus;

  if (status === EWarrantTaskStatus.Active || status === EWarrantTaskStatus.Rejected) {
    return status;
  }

  return null;
};

/**
 * The decisions Xcipio already holds, as the tabs should start out.
 *
 * Targets that arrived without a reference are skipped: they cannot be addressed in the
 * payload, so a decision could never be written for them — the same allowance
 * seedDocumentDecisions makes for a document with no docId.
 */
export const seedTargetDecisions = (services: EWarrantServices[]): TargetDecisions =>
  services.reduce((decisions: TargetDecisions, service) => {
    const decision = decisionOf(service);

    return (isAddressableTarget(service) && decision != null
      ? { ...decisions, [service.reference]: decision }
      : decisions);
  }, {});

/**
 * The decisions after one target's write came back successful.
 *
 * A plain set rather than a toggle like toggleDocumentDecision: by the time this is called
 * Xcipio has already recorded the decision, so there is no "back to undecided" state to
 * toggle into. Switching a target from Approved to Rejected is another write, and lands
 * here as another set.
 */
export const recordTargetDecision = (
  decisions: TargetDecisions,
  reference: string,
  status: EWarrantTaskStatus,
): TargetDecisions => ({ ...decisions, [reference]: status });

/** The decision standing against one target, or null when it is still undecided. */
export const getTargetDecision = (
  service: EWarrantServices,
  decisions: TargetDecisions,
): EWarrantTaskStatus | null => decisions[service.reference] ?? null;

/** Whether the operator has approved this target — its task is Active. */
export const isTargetApproved = (
  service: EWarrantServices,
  decisions: TargetDecisions,
): boolean => getTargetDecision(service, decisions) === EWarrantTaskStatus.Active;

/**
 * Whether every target of the e-warrant has been approved.
 *
 * Approving an e-warrant is approving every target it tasks, so anything less — a target
 * still awaiting a decision, or one the operator rejected — leaves the Approve button
 * closed. Rejecting the e-warrant is not gated this way; a rejection stands on its own.
 *
 * Vacuously true for an e-warrant with no services, so one stays approvable. A target that
 * cannot be decided - one that arrived without a reference, or one Xcipio has since expired -
 * is excluded rather than counted as undecided: neither can be addressed with a decision any
 * more, so gating on either would make the button impossible to press instead of merely
 * disabled — the same allowance areAllDocumentsApproved makes.
 */
export const areAllTargetsApproved = (
  services: EWarrantServices[],
  decisions: TargetDecisions,
): boolean =>
  services.filter(isTargetDecidable).every((service) => isTargetApproved(service, decisions));

/** A target standing in the way of approving the e-warrant, kept with the index the tabs number it by. */
export type BlockingTarget = {
  service: EWarrantServices
  index: number
  decision: EWarrantTaskStatus | null
};

/**
 * The targets standing in the way of approving the e-warrant — the rejected ones and the
 * ones still undecided — kept with the index the service tabs number them by, so the
 * warning can name them the way the Services tab does.
 *
 * A target that cannot be decided - unaddressable, or expired - is left out for the same
 * reason areAllTargetsApproved skips it: naming a target the operator has no way to decide
 * would explain a button that is not actually closed by it.
 */
export const getBlockingTargets = (
  services: EWarrantServices[],
  decisions: TargetDecisions,
): BlockingTarget[] =>
  services
    .map((service, index) => ({ service, index, decision: getTargetDecision(service, decisions) }))
    .filter(({ service, decision }) => isTargetDecidable(service) && decision !== EWarrantTaskStatus.Active);
